import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useUser } from 'hooks/useUser';

export interface SelfServeGate {
  /** May the user SEE the self-serve billing surfaces (any self-serve user). */
  allowed: boolean;
  isSelfServe: boolean;
  /**
   * May the user transact on billing — upgrade / change / manage the plan.
   * Non-purchasers see the surfaces but are directed to the billing admin.
   */
  canPurchase: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Returns whether the current user may see and transact on the self-serve
 * billing surfaces.
 *
 * Visibility gates on the account's `self_serve` Firestore field (set at user
 * creation by the newUser cloud function); enterprise users are always
 * excluded.
 *
 * Authority (`canPurchase`) comes from the backend `canManageBilling` — the
 * SAME predicate the money-path routes (checkout, billing-portal) enforce
 * (PLT-4289/PLT-4305). It is never re-derived from token role claims, which
 * can't tell who the account's recorded payer is, so the FE never offers an
 * upgrade/manage control the backend would 403.
 *
 * @returns A {@link SelfServeGate}: `allowed`/`isSelfServe` (may see self-serve
 * surfaces), `canPurchase` (billing-admin authority), and the underlying
 * account-info `isLoading` / `isError` so callers can fail closed while the
 * self-serve status is unknown.
 */
export const useSelfServeGate = (): SelfServeGate => {
  const { isEnterpriseUser } = useUser();
  const { data: accountInfo, isLoading, isError } = useAccountInfo();

  const isSelfServe = !isEnterpriseUser && (accountInfo?.selfServe ?? false);
  const allowed = isSelfServe;
  const canPurchase = allowed && (accountInfo?.canManageBilling ?? false);

  return { allowed, isSelfServe, canPurchase, isLoading, isError };
};
